/**
 * The ONE telemetry record of a dead-lettered visit report (either door): durable,
 * off-device and alertable through the failed-write capture — a dead letter is accepted,
 * but it is a visit nobody can attribute until the lead resolves, which an operator must
 * see. The record carries the KEY and the REASON CODE only: the server already holds the
 * full payload in core.projection_dead_letter, so no answer (rep input, PII) is copied.
 */

import { captureFailedWrite } from '../offline/failedWriteCapture'
import { QUEUE_TYPES } from '../offline/queueTypes'
import type { VisitReportDeadLetter } from './visitReportDeadLetter'
import type { VisitReportAckSourceEventMismatchError } from './missionVisitReports'

/** The capture reason every dead-letter record carries (triage filters on it). */
export const VISIT_REPORT_DEAD_LETTER_REASON = 'visit_report_dead_lettered'

/** The capture reason of a door answer that names ANOTHER write's key (Codex #2209 P2). */
export const VISIT_REPORT_ACK_MISMATCH_REASON = 'visit_report_ack_source_event_mismatch'

const KIND_BY_DOOR = { v2: QUEUE_TYPES.VISIT_REPORT_V2_SUBMIT, v8: QUEUE_TYPES.VISIT_REPORT_V8_SUBMIT } as const

/** Called by the TRANSPORT (one record per door answer, online or replayed — never twice). */
export function recordVisitReportDeadLetter(deadLetter: VisitReportDeadLetter): void {
  captureFailedWrite(
    KIND_BY_DOOR[deadLetter.door],
    { source_event_id: deadLetter.sourceEventId },
    VISIT_REPORT_DEAD_LETTER_REASON,
    { door: deadLetter.door, dead_letter_reason_code: deadLetter.reasonCode },
  )
}

/** The capture reason of a v8 dead letter that proves the hold but is not the generated model's shape. */
export const VISIT_REPORT_DEAD_LETTER_DRIFT_REASON = 'visit_report_dead_letter_shape_drift'
export const VISIT_REPORT_V8_DEAD_LETTER_SHAPE_DRIFT = 'VISIT_REPORT_V8_DEAD_LETTER_SHAPE_DRIFT'

/**
 * Called by the TRANSPORT, once per door answer, when a v8 dead letter is ACCEPTED as held (the flag, the reason
 * and this write's key) although its body differs from the generated VisitReportV8DeadLetter. The report stays
 * held; this is how the producer drift becomes visible. Field NAMES only, no value, no payload.
 */
export function recordVisitReportDeadLetterDrift(deadLetter: VisitReportDeadLetter, drift: { invalid: string[]; undeclared: string[] }): void {
  captureFailedWrite(
    KIND_BY_DOOR[deadLetter.door],
    { source_event_id: deadLetter.sourceEventId },
    VISIT_REPORT_DEAD_LETTER_DRIFT_REASON,
    { door: deadLetter.door, error_code: VISIT_REPORT_V8_DEAD_LETTER_SHAPE_DRIFT, invalid_fields: drift.invalid, undeclared_fields: drift.undeclared },
  )
}

/**
 * Called by the TRANSPORT, once per door answer, when that answer is a dead letter of ANOTHER
 * write: the server confirmed nothing about THIS one, so it stays unconfirmed (kept, retried) —
 * and an operator must see a door that answers for a key it was not asked about. Keyed on the
 * REQUEST's source_event_id (the write it concerns); the answered key rides in the meta.
 */
export function recordVisitReportAckMismatch(err: VisitReportAckSourceEventMismatchError): void {
  captureFailedWrite(
    KIND_BY_DOOR[err.door],
    { source_event_id: err.requestSourceEventId },
    VISIT_REPORT_ACK_MISMATCH_REASON,
    {
      door: err.door,
      error_code: err.code,
      answered_source_event_id: err.answeredSourceEventId,
      dead_letter_reason_code: err.deadLetterReasonCode,
    },
  )
}
